import { PauseIcon, PlayIcon, TrashIcon, PhoneIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams,useSearchParams } from 'react-router-dom';
import activeDot from '../../assets/agent-details/active-button-dot.svg';
import { DecorativeShape } from '../ui/DecorativeShape';
import banner2 from '../../assets/agents/banner-2.png';
import banner3 from '../../assets/agents/banner-3.png';
import banner5 from '../../assets/agents/banner-5.png';
import ActionsTab from './tabs/ActionsTab';
import AISettingsTab from './tabs/AISettingsTab';
import InstructionsTab from './tabs/InstructionsTab';
import KnowledgeBaseTab from './tabs/KnowledgeBaseTab';
import LogsAnalyticsTab from './tabs/LogsAnalyticsTab';
import QandATab from './tabs/QandATab';
import IntegrationsTab from './tabs/IntegrationsTab';
import VoiceConfigTab from './tabs/VoiceConfigTab';
import WhatsAppConfigTab from './tabs/WhatsAppConfigTab';
import { Container, Section } from '../layout/primitives';
import { Tabs } from '../ui/Tabs';
import LeadCaptureConfigTab from './tabs/LeadCaptureConfigTab';
import { agentInstanceService } from '../../services/agentService';
import { Button } from '../ui/Button';
import { Surface } from '../ui/Surface';

const mockAgent = {
  id: 'agent-001',
  name: 'Customer Support Agent',
  description: 'AI-powered customer support agent for handling inquiries and support requests',
  status: 'active',
  model: 'GPT-4',
  version: '2.6.7s',
  totalInteractions: 1250,
  successRate: 94.5,
  avgResponseTime: 1.8,
  capabilities: [],
  agentTypeId: '',
  agentType: null as any,
  department: { name: 'Marketing & Communication' },
  deletedAt: null as string | null,
  metadata: {} as Record<string, any>,
};

function mapApiToUi(data: any) {
  if (!data) return mockAgent;
  const aiSettings = data.aiSettings ?? null;
  const ai = aiSettings?.settings ?? {};

  return {
    id: data.id ?? '',
    name: data.name ?? data.code ?? 'Untitled Agent',
    description: data.description ?? mockAgent.description,
    status: typeof data.isActive === 'boolean' ? (data.isActive ? 'active' : 'inactive') : data.status ?? 'inactive',
    model: data.model ?? ai.model ?? mockAgent.model,
    version: data.version ? String(data.version) : String(aiSettings?.schemaVersion ?? '') || mockAgent.version,
    totalInteractions: data.metrics?.totalInteractions ?? mockAgent.totalInteractions,
    successRate: data.metrics?.successRate ?? mockAgent.successRate,
    avgResponseTime: data.metrics?.avgResponseTime ?? mockAgent.avgResponseTime,
    capabilities: (() => {
      // Capabilities can arrive as instance-level strings/objects or (most
      // commonly) on the agentType. Normalize any of these to code strings.
      const raw =
        (Array.isArray(data.capabilities) && data.capabilities.length && data.capabilities) ||
        (Array.isArray(data.capabilityList) && data.capabilityList.length && data.capabilityList) ||
        (Array.isArray(data.agentType?.capabilities) && data.agentType.capabilities) ||
        [];
      return (raw as any[])
        .map((c: any) => (typeof c === 'string' ? c : c?.capabilityCode ?? c?.code ?? ''))
        .filter(Boolean);
    })(),
    aiSettings: data.aiSettings ?? null,
    agentTypeId: data.agentTypeId ?? data.agentType?.id ?? '',
    agentType: data.agentType ?? (data.agentTypeId ? { id: data.agentTypeId } : null),
    department: data.department ?? null,
    deletedAt: data.deletedAt ?? null,
    metadata: data.metadata ?? {},
  };
}

interface TabConfig {
  id: string;
  name: string;
  component?: React.ComponentType<any>;
}

interface AgentConfigPanelProps {
  agentId: string;
  onDelete?: () => void;
  initialTab?: string;
}

export const AgentConfigPanel: React.FC<AgentConfigPanelProps> = ({ agentId, onDelete, initialTab = 'overview' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [agent, setAgent] = useState(mockAgent);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId) return;

    let mounted = true;
    setIsLoading(true);
    setError(null);

    const fetchAgent = async () => {
      try {
        const resp = await agentInstanceService.getAgentInstanceById(encodeURIComponent(agentId), { includeDeleted: true });
        const payload = (resp && resp.data) ? resp.data : resp;
        const mapped: any = mapApiToUi(payload);
        if (mounted) setAgent(mapped);
      } catch (err: any) {
        console.error('Failed to fetch agent instance', err);
        if (mounted) setError(err?.message ?? 'Failed to load agent');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchAgent();

    return () => { mounted = false; };
  }, [agentId]);

  const agentMeta = (agent as any)?.metadata || {};
  const entryType: string = agentMeta?.entryType || '';
  const isVoiceAgent =
    agent?.agentType?.code?.toLowerCase().includes('voice') ||
    entryType === 'inbound-voice' ||
    entryType === 'outbound-voice';
  const isWhatsAppAgent = entryType === 'whatsapp';

  const tabs: TabConfig[] = useMemo(() => {
    const baseTabs: TabConfig[] = [
      { id: 'overview', name: 'Overview' },
      { id: 'ai-settings', name: 'AI Settings', component: AISettingsTab },
      { id: 'instructions', name: 'Instructions', component: InstructionsTab },
      { id: 'integrations', name: 'Integrations', component: IntegrationsTab },
      { id: 'actions', name: 'Actions', component: ActionsTab },
      { id: 'knowledge-base', name: 'Knowledge Base', component: KnowledgeBaseTab },
      { id: 'q-and-a', name: 'Q & A', component: QandATab },
      { id: 'logs-analytics', name: 'Logs & Analytics', component: LogsAnalyticsTab },
    ];
    if (isVoiceAgent) {
      baseTabs.push({ id: 'voice-config', name: 'Voice Config', component: VoiceConfigTab });
    }
    if (isWhatsAppAgent) {
      baseTabs.push({ id: 'whatsapp-config', name: 'WhatsApp Config', component: WhatsAppConfigTab });
    }

    const hasLeadCapture = (agent.capabilities || []).some((c: any) => {
      const code = typeof c === 'string' ? c : c?.capabilityCode;
      return String(code || '').toLowerCase().replace(/_/g, '-') === 'lead-capture';
    });
    if (hasLeadCapture) {
      baseTabs.push({
        id: 'lead-capture',
        name: 'Lead Capture',
        component: LeadCaptureConfigTab,
      });
    }

    return baseTabs;
  }, [isVoiceAgent, isWhatsAppAgent, agent.capabilities]);

  const activeTabConfig = tabs.find((tab) => tab.id === activeTab);
  const ActiveComponent = activeTabConfig?.component;
  const isActive = agent.status === 'active';

  const displayedCapabilities = useMemo(() => (agent.capabilities || []).slice(0, 6), [agent.capabilities]);

  const handleStatusToggle = () => {
    setAgent((prev) => ({ ...prev, status: prev.status === 'active' ? 'inactive' : 'active' }));
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      onDelete?.();
    }
  };

  const renderOverview = () => (
    <section className="relative min-w-0">
      <div className="grid min-w-0 gap-12 xl:grid-cols-[minmax(0,1fr)_560px] xl:items-start">
        <div className="relative min-w-0">
          <div className="pointer-events-none absolute -left-[176px] top-[120px] hidden opacity-10 lg:block">
            <DecorativeShape className="h-[204px] w-[309px]" />
          </div>
          <div className="pointer-events-none absolute -left-[296px] top-[252px] hidden opacity-10 lg:block">
            <DecorativeShape className="h-[204px] w-[309px]" />
          </div>
          <div className="pointer-events-none absolute -left-[176px] top-[384px] hidden opacity-10 lg:block">
            <DecorativeShape className="h-[204px] w-[309px]" />
          </div>
          <div className="relative z-10 min-w-0">
            <h2 className="text-[2rem] font-semibold leading-[1.1] text-c-1f1f1f sm:text-[2.15rem]">Agent Details</h2>
            <div className="mt-6 grid min-w-0 grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2">
              <div className="min-w-0">
                <p className="text-base font-light leading-normal text-c-808080">Name</p>
                <p className="truncate text-[1.25rem] font-normal leading-normal text-c-1f1f1f">{agent.name}</p>
              </div>
              <div className="min-w-0">
                <p className="text-base font-light leading-normal text-c-808080">Category</p>
                <p className="truncate text-[1.25rem] font-normal leading-normal text-c-1f1f1f">
                  {agent.department?.name || 'Marketing & Communication'}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-base font-light leading-normal text-c-808080">Model</p>
                <p className="text-[1.25rem] font-normal leading-normal text-c-1f1f1f">{agent.model || 'GPT-4'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-base font-light leading-normal text-c-808080">Version</p>
                <p className="text-[1.25rem] font-normal leading-normal text-c-1f1f1f">{agent.version || '2.6.7s'}</p>
              </div>
            </div>
            <div className="mt-6 min-w-0">
              <p className="text-base font-light leading-normal text-c-808080">Capabilities</p>
              <div className="mt-2.5 flex min-w-0 flex-wrap gap-2.5">
                {displayedCapabilities.map((capability: string, idx: number) => (
                  <span
                    key={`${capability}-${idx}`}
                    className="rounded-[30px] bg-c-eeebf7 px-4 py-2 text-sm font-normal leading-normal text-c-6f74bd"
                  >
                    {capability}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid min-w-0 gap-6 sm:grid-cols-2 xl:gap-8">
          <Surface className="flex h-[60px] min-w-0 items-center justify-center rounded-[50px] border border-[#13b546] bg-white px-4">
            <span className="inline-flex items-center gap-2 text-[1.5rem] font-medium leading-none text-[#13b546]">
              <img src={activeDot} alt="" className="h-4 w-4" />
              {isActive ? 'Active' : 'Paused'}
            </span>
          </Surface>
          <article
            className="h-[200px] min-w-0 overflow-hidden rounded-[10px] bg-cover bg-center px-[22px] py-[16px]"
            style={{ backgroundImage: `url(${banner2})` }}
          >
            <div className="pt-[104px]">
              <p className="text-[1.25rem] font-normal leading-normal text-c-1f1f1f">Total Interactions</p>
              <p className="text-[2.625rem] font-bold leading-none text-c-1f1f1f">
                {Number(agent.totalInteractions || 0).toLocaleString()}
              </p>
            </div>
          </article>
          <article
            className="h-[200px] min-w-0 overflow-hidden rounded-[10px] bg-cover bg-center px-[22px] py-[16px]"
            style={{ backgroundImage: `url(${banner5})` }}
          >
            <div className="pt-[104px]">
              <p className="text-[1.25rem] font-normal leading-normal text-c-1f1f1f">Average Response time</p>
              <p className="text-[2.625rem] font-bold leading-none text-c-1f1f1f">
                {Number(agent.avgResponseTime || 0).toFixed(1)}s
              </p>
            </div>
          </article>
          <article
            className="h-[200px] min-w-0 overflow-hidden rounded-[10px] bg-cover bg-center px-[22px] py-[16px]"
            style={{ backgroundImage: `url(${banner3})` }}
          >
            <div className="pt-[104px]">
              <p className="text-[1.25rem] font-normal leading-normal text-c-1f1f1f">Success Rate</p>
              <p className="text-[2.625rem] font-bold leading-none text-c-1f1f1f">
                {Number(agent.successRate || 0).toFixed(1)}%
              </p>
            </div>
          </article>
        </div>
      </div>
      <div className="pointer-events-none absolute -left-[38px] bottom-[22px] hidden opacity-10 lg:block">
        <DecorativeShape className="h-[140px] w-[214px]" />
      </div>
    </section>
  );

  const renderTabBody = () => {
    if (isLoading) return <div className="tab-content-panel p-10 text-c-70768f">Loading configuration...</div>;
    if (error) return <div className="tab-content-panel border-rose-200 bg-rose-50 p-10 text-rose-600">{error}</div>;
    if (activeTab === 'overview') return renderOverview();
    if (ActiveComponent) {
      return (
        <div className="tab-content-panel">
          <ActiveComponent key={agent.id || 'agent'} agent={agent} />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="relative">
      <div className="relative z-10 px-6 pb-8 pt-6">
        <section className="min-w-0">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <header className="min-w-0">
              <h1 className="text-[2rem] font-medium leading-[1.1] text-c-1f1f1f sm:text-[2.5rem]">{agent.name || 'Customer Support Agent'}</h1>
              <p className="mt-2 max-w-4xl text-[1.25rem] font-light leading-normal text-c-1f1f1f">
                {agent.description || mockAgent.description}
              </p>
            </header>
            <div className="flex min-w-0 items-center justify-start gap-3 lg:justify-end">
              <Button tone="custom"
                type="button"
                onClick={handleStatusToggle}
                className="inline-flex items-center gap-2 rounded-xl border border-c-3683c6 bg-white px-3 py-2.5 text-base font-normal leading-none text-c-1f1f1f transition-colors hover:bg-c-f4f8ff"
              >
                {isActive ? <><span>Pause</span><PauseIcon className="h-4 w-4" /></> : <><span>Activate</span><PlayIcon className="h-4 w-4" /></>}
              </Button>
              {!agent.deletedAt && (
                <Button tone="custom"
                  type="button"
                  onClick={handleDelete}
                  className="inline-flex items-center gap-2 rounded-[10px] bg-[#ed6567] px-3 py-2.5 text-base font-normal leading-none text-white transition-opacity hover:opacity-95"
                >
                  Delete
                  <TrashIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <Tabs
            variant="config"
            className="mt-6"
            items={tabs.map((tab) => ({ id: tab.id, label: tab.name }))}
            value={activeTab}
            onChange={setActiveTab}
          />
        </section>
        <Section className="pt-6">{renderTabBody()}</Section>
      </div>
    </div>
  );
};

export default AgentConfigPanel;
